import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Popconfirm } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import {
  createApiKeyApi,
  deleteApiKeyApi,
  getApiKeyPageApi,
  updateApiKeyNameApi,
} from "../../service/apiKey";
import { AutoModel } from "./AutoModel";
import "./ApiKeyModal.scss";

export type ApiKeyItem = ApiKey.ApiKeyRespVO;

export interface ApiKeyModalProps {
  open: boolean;
  onClose: () => void;
}

function formatApiKeyMaskDisplay(mask: string): string {
  if (!mask) return "-";
  if (!mask.includes("*")) return mask;
  return mask.replace(/\*+/g, "••••••••••••••••");
}

function formatCreateTime(value: string): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return value.length >= 10 ? value.slice(0, 10) : value;
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fallback below
  }

  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
}

const ApiKeyIcon: FC<{ size?: number }> = ({ size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden
  >
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

export const ApiKeyModal: FC<ApiKeyModalProps> = ({ open, onClose }) => {
  const [keys, setKeys] = useState<ApiKeyItem[]>([]);
  const [newKeyPlain, setNewKeyPlain] = useState<string | null>(null);
  const [newKeyCreatedId, setNewKeyCreatedId] = useState<number | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingNameDraft, setEditingNameDraft] = useState("");
  const [creating, setCreating] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const editingInputRef = useRef<HTMLInputElement | null>(null);

  const refreshKeys = useCallback(async () => {
    try {
      const res = await getApiKeyPageApi({ pageNo: 1, pageSize: 100 });
      if (res.code === 0 && Array.isArray(res.data?.list)) {
        setKeys(res.data.list);
      }
    } catch {
      // systemRequest 已处理错误提示
    }
  }, []);

  useEffect(() => {
    if (!editingId) return;
    editingInputRef.current?.focus();
    editingInputRef.current?.select();
  }, [editingId]);

  useEffect(() => {
    if (!open) return;
    void refreshKeys();
    setNewKeyPlain(null);
    setNewKeyCreatedId(null);
    setShowNewForm(false);
    setNewKeyName("");
    setCopiedKey(null);
    setEditingId(null);
    setEditingNameDraft("");
  }, [open, refreshKeys]);

  const handleClose = () => {
    setShowNewForm(false);
    setNewKeyPlain(null);
    setNewKeyCreatedId(null);
    setEditingId(null);
    setEditingNameDraft("");
    onClose();
  };

  const commitEditing = async (item: ApiKeyItem, draft: string) => {
    const trimmed = draft.trim() || item.name;
    if (trimmed === item.name) {
      setEditingId(null);
      setEditingNameDraft("");
      return;
    }
    if (savingName) return;
    setSavingName(true);
    try {
      const res = await updateApiKeyNameApi({ id: item.id, name: trimmed });
      if (res.code === 0) {
        setEditingId(null);
        setEditingNameDraft("");
        await refreshKeys();
      }
    } catch {
      // systemRequest 已处理错误提示
    } finally {
      setSavingName(false);
    }
  };

  const startEditing = (item: ApiKeyItem) => {
    if (editingId !== null && editingId !== item.id) {
      const prev = keys.find((k) => k.id === editingId);
      if (prev) {
        void commitEditing(prev, editingNameDraft);
      }
    }
    setEditingId(item.id);
    setEditingNameDraft(item.name);
  };

  const finishEditing = (item: ApiKeyItem) => {
    if (editingId !== item.id) return;
    void commitEditing(item, editingNameDraft);
  };

  const cancelEditing = (item: ApiKeyItem) => {
    if (editingId !== item.id) return;
    setEditingId(null);
    setEditingNameDraft("");
  };

  const handleCopy = async (text: string, token: string) => {
    const ok = await copyToClipboard(text);
    if (!ok) return;
    setCopiedKey(token);
    window.setTimeout(() => {
      setCopiedKey((prev) => (prev === token ? null : prev));
    }, 1800);
  };

  const handleToggleNewForm = () => {
    setShowNewForm((prev) => !prev);
    if (showNewForm) {
      setNewKeyName("");
    }
  };

  const handleCreateKey = async () => {
    const name = newKeyName.trim();
    if (!name) {
      Modal.warning({ title: "提示", content: "请输入密钥名称" });
      return;
    }
    if (creating) return;
    setCreating(true);
    try {
      const res = await createApiKeyApi({ name });
      if (res.code === 0 && res.data?.apiKey) {
        setNewKeyPlain(res.data.apiKey);
        setNewKeyCreatedId(res.data.id ?? null);
        setShowNewForm(false);
        setNewKeyName("");
        await refreshKeys();
      }
    } catch {
      // systemRequest 已处理错误提示
    } finally {
      setCreating(false);
    }
  };

  const confirmDeleteKey = async (item: ApiKeyItem) => {
    if (editingId === item.id) {
      setEditingId(null);
      setEditingNameDraft("");
    }
    try {
      const res = await deleteApiKeyApi(item.id);
      if (res.code === 0) {
        if (newKeyCreatedId === item.id) {
          setNewKeyPlain(null);
          setNewKeyCreatedId(null);
        }
        await refreshKeys();
      }
    } catch {
      // systemRequest 已处理错误提示
    }
  };

  const showEmpty = keys.length === 0 && !newKeyPlain && !showNewForm;

  return (
    <AutoModel
      open={open}
      onClose={handleClose}
      size="xl"
      contentMaxWidth={860}
      bodyClassName="api-key-modal__body"
      bodyStyle={{ paddingTop: 0, paddingBottom: 0 }}
      title={
        <div className="api-key-modal__title-row">
          <span>API 密钥</span>
          <span className="api-key-modal__subtitle">密钥仅在创建时完整显示，请妥善保管</span>
        </div>
      }
      footerRight={<>
        <Button onClick={handleClose}>取消</Button>
        <Button type="primary" className="api-key-modal__create-btn" onClick={handleToggleNewForm}>
          <PlusOutlined />
          创建新密钥
        </Button>
      </>}
    >
      <div className="api-key-modal__scroll">
        {showNewForm ? (
          <div className="api-key-modal__new-form">
            <label htmlFor="apikey-name-input">新建密钥</label>
            <input
              id="apikey-name-input"
              className="api-key-modal__name-input"
              type="text"
              placeholder="输入名称，例如：生产环境、测试脚本"
              value={newKeyName}
              maxLength={50}
              disabled={creating}
              onChange={(e) => setNewKeyName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleCreateKey();
              }}
              autoFocus
            />
            <div className="form-actions">
              <button
                type="button"
                className="api-key-modal__form-btn api-key-modal__form-btn--primary"
                disabled={creating}
                onClick={() => void handleCreateKey()}
              >
                创建
              </button>
              <button
                type="button"
                className="api-key-modal__form-btn api-key-modal__form-btn--ghost"
                disabled={creating}
                onClick={() => {
                  setShowNewForm(false);
                  setNewKeyName("");
                }}
              >
                取消
              </button>
            </div>
          </div>
        ) : null}

        {newKeyPlain ? (
          <div className="api-key-modal__new-banner">
            <div className="banner-header">
              <SafetyOutlined />
              新密钥已生成 — 请立即复制，关闭后将无法再次查看完整密钥
            </div>
            <div className="key-text">{newKeyPlain}</div>
            <button
              type="button"
              className={`btn-copy-big${copiedKey === `banner-${newKeyPlain}` ? " is-copied" : ""}`}
              onClick={() => void handleCopy(newKeyPlain, `banner-${newKeyPlain}`)}
            >
              {copiedKey === `banner-${newKeyPlain}` ? "已复制" : "复制密钥"}
            </button>
          </div>
        ) : null}

        {showEmpty ? (
          <div className="api-key-modal__empty">
            <div className="empty-icon">
              <ApiKeyIcon />
            </div>
            <p>暂无 API 密钥，创建一个以通过 API 访问服务</p>
          </div>
        ) : null}

        {keys.length > 0 ? (
          <table className="api-key-modal__table">
            <thead>
              <tr>
                <th style={{ width: "14%" }}>名称</th>
                <th style={{ width: "42%" }}>密钥</th>
                <th style={{ width: "14%" }}>创建时间</th>
                <th style={{ width: "30%", textAlign: "right" }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((item) => {
                const isEditing = editingId === item.id;
                return (
                  <tr key={item.id} className={isEditing ? "is-editing-name" : undefined}>
                    <td>
                      {isEditing ? (
                        <input
                          ref={editingInputRef}
                          className="api-key-modal__col-name-input"
                          type="text"
                          value={editingNameDraft}
                          maxLength={50}
                          disabled={savingName}
                          aria-label="编辑名称"
                          onChange={(e) => setEditingNameDraft(e.target.value)}
                          onBlur={() => finishEditing(item)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              finishEditing(item);
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              cancelEditing(item);
                            }
                          }}
                        />
                      ) : (
                        <div className="col-name">{item.name}</div>
                      )}
                    </td>
                    <td>
                      <span className="col-key">{formatApiKeyMaskDisplay(item.apiKeyMask)}</span>
                    </td>
                    <td>
                      <span className="col-date">{formatCreateTime(item.createTime)}</span>
                    </td>
                    <td className="col-actions">
                      <button
                        type="button"
                        className={`btn-rename btn-icon${isEditing ? " is-active" : ""}`}
                        title="编辑"
                        aria-label="编辑名称"
                        aria-pressed={isEditing}
                        onClick={() => startEditing(item)}
                      >
                        <EditOutlined />
                      </button>
                      <Popconfirm
                        title="确定要删除这个密钥吗？此操作不可恢复。"
                        okText="确定"
                        cancelText="取消"
                        zIndex={1100}
                        onConfirm={() => void confirmDeleteKey(item)}
                      >
                        <button
                          type="button"
                          className="btn-delete btn-icon"
                          title="删除"
                          aria-label="删除"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <DeleteOutlined />
                        </button>
                      </Popconfirm>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}
      </div>
    </AutoModel>
  );
};
